import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Calendar, DollarSign, Bell, CheckCircle, Clock } from 'lucide-react';
import { CalendarEvent } from '../types';
import { calendarEventService } from '../services/database';
import { formatCurrency } from '../utils/helpers';
import { ConfirmModal } from '../components/ConfirmModal';

export const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    event_date: new Date().toISOString().split('T')[0],
    event_type: 'bill' as 'bill' | 'payment' | 'reminder' | 'goal',
    is_recurring: false,
    recurrence_pattern: 'monthly' as 'monthly' | 'weekly' | 'yearly',
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const eventsData = await calendarEventService.getCalendarEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.event_date) return;

    try {
      const eventData = {
        user_id: 'user1', // Replace with actual user ID
        title: formData.title,
        description: formData.description,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        event_date: formData.event_date,
        event_type: formData.event_type,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : undefined,
        is_completed: false,
        currency: 'USD',
      };

      if (editingEvent) {
        await calendarEventService.updateCalendarEvent(editingEvent.id, eventData as any);
      } else {
        await calendarEventService.createCalendarEvent(eventData as any);
      }

      setFormData({
        title: '',
        description: '',
        amount: '',
        event_date: new Date().toISOString().split('T')[0],
        event_type: 'bill',
        is_recurring: false,
        recurrence_pattern: 'monthly',
      });
      setShowAddEvent(false);
      setEditingEvent(null);
      loadEvents();
    } catch (error) {
      console.error('Error saving calendar event:', error);
    }
  };

  const handleDelete = (eventId: string) => {
    setConfirmDeleteId(eventId);
  };
  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await calendarEventService.deleteCalendarEvent(confirmDeleteId);
      loadEvents();
    } catch (error) {
      console.error('Error deleting calendar event:', error);
    } finally {
      setConfirmDeleteId(null);
    }
  };
  const cancelDelete = () => setConfirmDeleteId(null);

  const handleEdit = (event: CalendarEvent) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      amount: event.amount?.toString() || '',
      event_date: event.event_date,
      event_type: event.event_type as 'bill' | 'payment' | 'reminder' | 'goal',
      is_recurring: event.is_recurring,
      recurrence_pattern: (event.recurrence_pattern as 'monthly' | 'weekly' | 'yearly') || 'monthly',
    });
    setShowAddEvent(true);
  };

  const handleToggleComplete = async (eventId: string, isCompleted: boolean) => {
    try {
      await calendarEventService.updateCalendarEvent(eventId, { is_completed: !isCompleted });
      loadEvents();
    } catch (error) {
      console.error('Error updating event completion:', error);
    }
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'bill':
        return <DollarSign className="w-4 h-4 text-red-600" />;
      case 'payment':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'reminder':
        return <Bell className="w-4 h-4 text-blue-600" />;
      case 'goal':
        return <CheckCircle className="w-4 h-4 text-purple-600" />;
      default:
        return <Calendar className="w-4 h-4 text-gray-600" />;
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'bill':
        return 'border-red-200 bg-red-50';
      case 'payment':
        return 'border-green-200 bg-green-50';
      case 'reminder':
        return 'border-blue-200 bg-blue-50';
      case 'goal':
        return 'border-purple-200 bg-purple-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    return events
      .filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= today && eventDate <= nextWeek && !event.is_completed;
      })
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
  };

  const getOverdueEvents = () => {
    const today = new Date();
    return events
      .filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate < today && !event.is_completed;
      })
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
  };

  const upcomingEvents = getUpcomingEvents();
  const overdueEvents = getOverdueEvents();

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-2 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Calendar & Reminders</h1>
          <p className="text-gray-600">Track upcoming bills, payments, and important dates</p>
        </div>
        <button
          onClick={() => setShowAddEvent(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Event
        </button>
      </div>

      {/* Overdue Events */}
      {overdueEvents.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Overdue Events
          </h2>
          <div className="space-y-3">
            {overdueEvents.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                <div className="flex items-center gap-3">
                  {getEventIcon(event.event_type)}
                  <div>
                    <h3 className="font-medium text-gray-900">{event.title}</h3>
                    <p className="text-sm text-gray-600">
                      {new Date(event.event_date).toLocaleDateString()} • {event.event_type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {event.amount && (
                    <span className="font-semibold text-gray-900">{formatCurrency(event.amount)}</span>
                  )}
                  <button
                    onClick={() => handleToggleComplete(event.id, event.is_completed)}
                    className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(event)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Upcoming Events (Next 7 Days)
        </h2>
        {upcomingEvents.length > 0 ? (
          <div className="space-y-3">
            {upcomingEvents.map((event) => (
              <div key={event.id} className={`flex items-center justify-between p-4 rounded-lg border ${getEventColor(event.event_type)}`}>
                <div className="flex items-center gap-3">
                  {getEventIcon(event.event_type)}
                  <div>
                    <h3 className="font-medium text-gray-900">{event.title}</h3>
                    <p className="text-sm text-gray-600">
                      {new Date(event.event_date).toLocaleDateString()} • {event.event_type}
                      {event.is_recurring && ` • ${event.recurrence_pattern}`}
                    </p>
                    {event.description && (
                      <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {event.amount && (
                    <span className="font-semibold text-gray-900">{formatCurrency(event.amount)}</span>
                  )}
                  <button
                    onClick={() => handleToggleComplete(event.id, event.is_completed)}
                    className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(event)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-8">No upcoming events in the next 7 days</p>
        )}
      </div>

      {/* All Events */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Events</h2>
        {events.length > 0 ? (
          <div className="space-y-3">
            {events
              .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
              .map((event) => (
                <div key={event.id} className={`flex items-center justify-between p-4 rounded-lg border ${getEventColor(event.event_type)} ${event.is_completed ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-3">
                    {getEventIcon(event.event_type)}
                    <div>
                      <h3 className={`font-medium ${event.is_completed ? 'line-through' : 'text-gray-900'}`}>
                        {event.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {new Date(event.event_date).toLocaleDateString()} • {event.event_type}
                        {event.is_recurring && ` • ${event.recurrence_pattern}`}
                      </p>
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {event.amount && (
                      <span className="font-semibold text-gray-900">{formatCurrency(event.amount)}</span>
                    )}
                    <button
                      onClick={() => handleToggleComplete(event.id, event.is_completed)}
                      className={`p-1 transition-colors ${event.is_completed ? 'text-green-600' : 'text-gray-400 hover:text-green-600'}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(event)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-8">No events found. Add your first event to get started!</p>
        )}
      </div>

      {/* Add/Edit Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingEvent ? 'Edit Event' : 'Add Event'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Rent Payment"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Add details about this event..."
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (Optional)
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Date
                </label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="bill">Bill</option>
                  <option value="payment">Payment</option>
                  <option value="reminder">Reminder</option>
                  <option value="goal">Goal</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                  Recurring Event
                </label>
              </div>
              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recurrence Pattern
                  </label>
                  <select
                    value={formData.recurrence_pattern}
                    onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingEvent ? 'Update' : 'Create'} Event
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddEvent(false);
                    setEditingEvent(null);
                    setFormData({
                      title: '',
                      description: '',
                      amount: '',
                      event_date: new Date().toISOString().split('T')[0],
                      event_type: 'bill',
                      is_recurring: false,
                      recurrence_pattern: 'monthly',
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!confirmDeleteId}
        message="Are you sure you want to delete this event?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}; 